import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { pct } from "../../lib/format";
import type { ComponentRow } from "../../lib/demographics";

const NATURAL = "#7a5195";
const MIGRATION_UP = "#2f6df6";
const MIGRATION_DOWN = "#cf3a2e";

/**
 * Why the population moved, period by period.
 *
 * Two stacked bars per intercensal period: births minus deaths, and everyone
 * who arrived minus everyone who left. Their sum is the growth rate, which is
 * why they are stacked rather than drawn side by side — the bar's total height
 * is the number people actually quote.
 *
 * Migration is coloured by sign, natural change is not, because the question
 * the chart answers is which of the two is dragging and in which direction.
 */
export function ComponentsChart({ rows }: { rows: ComponentRow[] }) {
  const data = rows.map((r) => ({
    label: r.label,
    natural: r.natural * 100,
    migration: r.migration * 100,
    growth: r.growth * 100,
  }));

  return (
    <div className="h-32 w-full">
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -14 }} stackOffset="sign">
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={34}
            tickFormatter={(v: number) => `${v > 0 ? "+" : ""}${v.toFixed(1)}`}
          />
          <ReferenceLine y={0} stroke="#5a6472" strokeOpacity={0.5} />
          <Tooltip
            cursor={{ fill: "#00000008" }}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            formatter={(value, name) => [
              `${(value as number) > 0 ? "+" : ""}${(value as number).toFixed(2)}%/yr`,
              name === "natural" ? "births − deaths" : "arrivals − departures",
            ]}
            labelFormatter={(l, payload) => {
              const r = payload?.[0]?.payload as (typeof data)[number] | undefined;
              return r ? `${l}  ·  total ${pct(r.growth / 100)}/yr` : String(l);
            }}
          />
          <Bar dataKey="natural" stackId="a" fill={NATURAL} isAnimationActive={false} />
          <Bar dataKey="migration" stackId="a" isAnimationActive={false}>
            {data.map((r) => (
              <Cell
                key={r.label}
                fill={r.migration >= 0 ? MIGRATION_UP : MIGRATION_DOWN}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** The chart's key, inline — three swatches read faster than a legend block. */
export function ComponentsKey() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-ink-soft">
      <Swatch color={NATURAL} label="births − deaths" />
      <Swatch color={MIGRATION_UP} label="people arriving" />
      <Swatch color={MIGRATION_DOWN} label="people leaving" />
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}
